import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch as MuiSwitch,
  FormControlLabel,
  CardContent,
  Autocomplete,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  styled,
  Switch,
  CircularProgress,
  Checkbox,
  Tabs,
  Tab
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LoadingButton from '@mui/lab/LoadingButton';
import PageTitle from '@Components/PageTitle/PageTitle';
import * as DefaultRoutes from '@Routes/DefaultRoutes';
import * as APIRoutes from '@API/APIRoutes';
import themeCreator from '@Themes/themeCreator';
import { StyledCard, SectionTitle, ValueText } from '@Pages/RegulatoryMember/ExitRequestDetail/styles';
import usePostItem from '@Hooks/usePostItem';
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import axiosInstance from '@API/axios';
import useGetAll from '@Hooks/useGetAll';
import { useNavigationGuard } from '@Hooks/useNavigationGuard';
import { DraftConfirmationDialog } from '@Components/DraftConfirmationDialog/DraftConfirmationDialog';
import ExcelBulkImport from '@Components/ExcelBulkImport';
import {
  PageContainer,
  HeaderContainer,
  ContentGridContainer,
  SubmitButtonContainer,
  cardTransition,
  cardContentStyles,
  sectionTitleStyles,
  labelTextStyles,
  valueTextStyles,
  smallChipStyles,
  statusChipStyles,
  actionButtonStyles,
  addButtonStyles,
  dialogButtonStyles,
  cancelDialogButtonStyles,
  descriptionPaperStyles,
  tableHeaderCellStyles,
  tableCellStyles,
  dialogTitleStyles,
  infoAlertStyles,
  noCommentStyles
} from './ExitRequestResponseStyles';
import { setSnackbar } from "../../../Redux/snackBarSlice";
import { useDispatch, useSelector } from "react-redux";
import { setData, setLastSavedAt as setReduxLastSavedAt } from "@Redux/requestDataSlice";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import { getRestrictionTypeColor } from "../../../Utils/Validations/HelperFunctions/StyleFunction";
import ExtensionRequestDialog from "@Components/ExtensionRequestDialog/ExtensionRequestDialog";
import ExtensionStatusBanner from "@Components/ExtensionRequestDialog/ExtensionStatusBanner";
import { getImpactStatusConfig, IMPACT_STATUS, isToBeDetermined } from '@Utils/impactStatus';

const PRODUCT_IDENTIFIER_LABEL = 'CUC / FG / Packaging Code';
const PRODUCT_IDENTIFIER_HEADER = 'Product Code';

// Mode Switch Component
const ModeSwitch = styled((props) => (
  <MuiSwitch
    focusVisibleClassName=".Mui-focusVisible"
    disableRipple
    {...props}
  />
))(({ theme }) => ({
  width: 34,
  height: 18,
  padding: 0,
  margin: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1,
    transitionDuration: '250ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
        opacity: 0.85,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 250,
    }),
  },
}));

const ExitRequestResponseUnified = () => {
  const { reqId, categoryAssignmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();

  // Hooks
  const { loading: submitting, postItem } = usePostItem();
  const { loading: loadingProducts, getAll } = useGetAll();

  // Redux state
  const requestDataFromRedux = useSelector(state => state.requestData.requestData);
  const lastSavedAtFromRedux = useSelector(state => state.requestData.lastSavedAt);
  const userRegions = useSelector((state) => state.user.regions || []);
  const currentUserRole = useSelector((state) => state.user.role);

  // Mode management
  const [isEditMode, setIsEditMode] = useState(true);
  const [editDisabled, setEditDisabled] = useState(false);

  // Data states
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [responseId, setResponseId] = useState(null);
  const [ingredient, setIngredient] = useState({});
  const [user, setUser] = useState({});
  const [exitPlans, setExitPlans] = useState([]);
  const [createdAt, setCreatedAt] = useState(null);
  const [status, setStatus] = useState(null);
  const [originalResponseData, setOriginalResponseData] = useState(null);

  // Restrictions Descriptions
  const [categoryDescription, setCategoryDescription] = useState(null);

  // Form states
  const [responseStatus, setResponseStatus] = useState('compliant');
  const [feedback, setFeedback] = useState('');
  const [affectedProducts, setAffectedProducts] = useState([]);
  const [initialState, setInitialState] = useState(null);
  const [submitType, setSubmitType] = useState(null);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [showSubmitConfirmDialog, setShowSubmitConfirmDialog] = useState(false);
  const [pendingSubmitCreatePlan, setPendingSubmitCreatePlan] = useState(false);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);

  // Draft-related states
  const [isDraft, setIsDraft] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Coordinator-lock: true when this is a UI exit-request and the RD Coordinator hasn't submitted yet
  const [isCoordinatorLocked, setIsCoordinatorLocked] = useState(false);

  // Navigation guard (only handles browser refresh/close)
  useNavigationGuard(hasUnsavedChanges, 'You have unsaved changes. Please save as draft or submit your impact assessment before leaving.');
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Product dialog states
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAffected, setIsAffected] = useState(true);
  const [productComment, setProductComment] = useState('');
  const [productCommentError, setProductCommentError] = useState('');
  const [productBrand, setProductBrand] = useState('');
  const [productCountries, setProductCountries] = useState([]);
  const [addingCustomProduct, setAddingCustomProduct] = useState(false);

  // Custom product states
  const [isCustomProduct, setIsCustomProduct] = useState(false);
  const [customProductName, setCustomProductName] = useState('');
  const [customProductCUC, setCustomProductCUC] = useState('');
  const [customProductErrors, setCustomProductErrors] = useState({
    name: '',
    cuc: ''
  });

  // Delete popup states
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePopupContent, setDeletePopupContent] = useState("");

  // Edit product dialog states
  const [showEditProductDialog, setShowEditProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductIsAffected, setEditProductIsAffected] = useState(null);
  const [editProductComment, setEditProductComment] = useState('');
  const [editProductBrand, setEditProductBrand] = useState('');
  const [editProductCountries, setEditProductCountries] = useState([]);

  // Navigation context helpers
  // Note the RD Manager prefix is "rdm-incoming-requests", which does NOT contain
  // "rd-incoming-requests", so it has to be matched explicitly.
  const isFromIncomingRequests = useMemo(() =>
    location.state?.from === 'requests'
    || location.pathname.includes('rd-incoming-requests')
    || location.pathname.includes('rdm-incoming-requests'),
    [location]
  );

  const isFromResponses = useMemo(() =>
    location.state?.from === 'responses' || location.pathname.includes('rd-responses') || location.pathname.includes('rdm-responses'),
    [location]
  );

  const isFromEscalatedRequests = useMemo(() =>
    location.pathname.includes('escalated-requests'),
    [location]
  );

  // Check if changes have been made
  const isChanged = useMemo(() =>
    isEditMode && initialState && (
      responseStatus !== initialState.responseStatus ||
      feedback !== initialState.feedback ||
      JSON.stringify(affectedProducts) !== initialState.affectedProducts
    ),
    [isEditMode, initialState, responseStatus, feedback, affectedProducts]
  );

  // Track unsaved changes for draft functionality
  useEffect(() => {
    if (initialState) {
      // For existing responses, check if anything changed from initial state
      const hasChanges =
        responseStatus !== initialState.responseStatus ||
        feedback !== initialState.feedback ||
        JSON.stringify(affectedProducts) !== initialState.affectedProducts;
      setHasUnsavedChanges(hasChanges);
    } else {
      // For new responses, consider it unsaved if there are products, feedback, or non-default status
      const hasContent =
        affectedProducts.length > 0 ||
        feedback.trim() !== '' ||
        responseStatus !== 'compliant';
      setHasUnsavedChanges(hasContent);
    }
  }, [responseStatus, feedback, affectedProducts, initialState]);

  // Handle navigation blocker
  // Navigation dialog handlers
  const handleSaveDraftAndNavigate = async () => {
    const result = await handleSaveDraft();
    if (result === 'failed') {
      return;
    }
    setShowNavigationDialog(false);
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleDiscardAndNavigate = () => {
    setHasUnsavedChanges(false);
    setShowNavigationDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationDialog(false);
    setPendingNavigation(null);
  };

  // Data fetching effect
  useEffect(() => {
    const fetchData = async () => {
      // Fetch available products
      getAll('/product/all', 'Failed to fetch products').then(res => {
        if (res) {
          const products = (res || []).map(p => ({
            productId: p.id,
            productCucNumber: p.cucNumber,
            productName: p.name
          }));
          setAvailableProducts(products);
        }
      });

      // Check if data exists in Redux
      if (
        requestDataFromRedux &&
        requestDataFromRedux.exitRequest &&
        requestDataFromRedux.exitRequest.id === reqId &&
        requestDataFromRedux.categoryAssignmentId === categoryAssignmentId
      ) {
        loadResponseData(requestDataFromRedux);
        setLoading(false);
      } else {
        // Fetch from API
        try {
          setLoading(true);
          const res = await axiosInstance.get(`/exit-request-response/my-submissions/${reqId}/${categoryAssignmentId}`);
          console.log(res.data);
          if (!res.data.exitRequest || res.data.exitRequest === null) {
            setNotFound(true);
            dispatch(setSnackbar({ open: true, message: "No Impact Assessment Found.", error: true }));
            setLoading(false);
            return;
          }

          loadResponseData(res.data);
          dispatch(setData(res.data));
          setLoading(false);
        } catch (error) {
          console.error(error);
          setLoading(false);
          setNotFound(true);
          dispatch(setSnackbar({ open: true, message: "Failed to fetch Impact Assessment.", error: true }));
        }
      }
    };

    fetchData();
  }, [reqId, categoryAssignmentId]);

  // Helper function to load response data
  const loadResponseData = (data) => {
    setOriginalResponseData(data);
    setRequestData(data.exitRequest);
    setIngredient(data.exitRequest.ingredient);
    setUser(data.exitRequest.user);
    setCategoryDescription(data.categoryDescription || null);

    // ── Coordinator-lock ──────────────────────────────────────────────
    // When the backend returns locked=true the RD Coordinator has not yet
    // submitted their product list; block the member from responding.
    const coordinatorLocked = !!data.locked;
    setIsCoordinatorLocked(coordinatorLocked);
    // Force view-only mode when locked so no editing is possible
    if (coordinatorLocked) {
      setIsEditMode(false);
    }

    if (data.id) {
      setResponseId(data.id);
      // NOTE: do NOT set isEditMode(true) here — the coordinator-lock check above
      // may have forced isEditMode(false), and the toggle in the header is the
      // only way for the user to enter edit mode (it's hidden when locked).
      const initialResponseStatus =
        data.isCompliant === null || data.isCompliant === undefined
          ? "tbd"
          : data.isCompliant
            ? "compliant"
            : "incompliant";
      setResponseStatus(initialResponseStatus);
      setFeedback(data.additionalFeedback || '');
      setAffectedProducts(data.products || []);
      setExitPlans(data.exitPlans || []);
      setCreatedAt(data.createdAt);
      setStatus(data.status);

      // Check if this is a draft
      if (data.status === "DRAFT") {
        setIsDraft(true);
        // Try to get saved date from Redux first, then from API data
        const savedDate = lastSavedAtFromRedux || data.updatedAt || data.createdAt;
        setLastSavedAt(savedDate);
        dispatch(setReduxLastSavedAt(savedDate));
      }

      setInitialState({
        responseStatus: initialResponseStatus,
        feedback: data.additionalFeedback || '',
        affectedProducts: JSON.stringify(data.products || [])
      });

      // Disable edit if status is COMPLETED
      if (data.status === "COMPLETED") {
        setEditDisabled(true);
        setIsEditMode(false);
      } else {
        setEditDisabled(false);
      }
      // Always start in view mode; user can toggle to edit via the header switch
      setIsEditMode(false);
    }

  };

  // Navigation handlers
  /**
   * Where "Return" goes. Falls back to the browser's previous page so the button always does
   * something, even on a route none of the source flags recognise.
   */
  const goBackToSource = () => {
    if (isFromEscalatedRequests) {
      navigate(DefaultRoutes.ApproverEscalatedRequests);
    } else if (isFromIncomingRequests) {
      navigate(currentUserRole === 'RD_MANAGER' ? DefaultRoutes.rdManagerIncomingRequests : DefaultRoutes.rdIncomingRequests);
    } else if (isFromResponses) {
      navigate(currentUserRole === 'RD_MANAGER' ? DefaultRoutes.rdManagerResponses : DefaultRoutes.rdResponses);
    } else {
      navigate(-1);
    }
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      // Show dialog and store the navigation action
      setPendingNavigation(() => goBackToSource);
      setShowNavigationDialog(true);
    } else {
      goBackToSource();
    }
  };

  const handleModeToggle = () => {
    if (!editDisabled) {
      setIsEditMode(!isEditMode);
    }
  };

  const getExitPlanCreateRoute = () => {
    let route;
    if (isFromEscalatedRequests) {
      route = DefaultRoutes.ApproverExitPlanCreate;
    } else if (currentUserRole === 'RD_MANAGER') {
      route = DefaultRoutes.rdManagerExitPlanCreate;
    } else {
      route = DefaultRoutes.rdExitPlanCreate;
    }
    return route.replace(':reqId', reqId).replace(':categoryAssignmentId', categoryAssignmentId);
  };

  const handleViewExitPlanClick = () => {
    navigate(getExitPlanCreateRoute());
  };

  const handleCreateExitPlanClick = () => {
    navigate(getExitPlanCreateRoute());
  };

  const getExemptionCreateRoute = () => {
    let route;
    if (isFromEscalatedRequests) {
      route = DefaultRoutes.ApproverExemptionCreate;
    } else if (currentUserRole === 'RD_MANAGER') {
      route = DefaultRoutes.rdManagerExemptionCreate;
    } else {
      route = DefaultRoutes.rdExemptionCreate;
    }
    return route.replace(':reqId', reqId).replace(':categoryAssignmentId', categoryAssignmentId);
  };

  const handleCreateExemptionClick = () => {
    navigate(getExemptionCreateRoute());
  };

  // Product management
  const handleAddProduct = async () => {
    if (isCustomProduct) {
      let errors = {};
      let hasError = false;

      if (!customProductName.trim()) {
        errors.name = "Product name is required";
        hasError = true;
      }

      if (!customProductCUC.trim()) {
        errors.cuc = "CUC number is required";
        hasError = true;
      } else {
        // Check for duplicate CUC number (with trimmed comparison)
        const trimmedCucNumber = customProductCUC.trim();
        const existingProduct = availableProducts.find(
          (prod) => prod.productCucNumber && prod.productCucNumber.trim() === trimmedCucNumber
        );
        if (existingProduct) {
          errors.cuc = `A product with CUC number "${trimmedCucNumber}" already exists: ${existingProduct.productName}`;
          hasError = true;
        }
      }

      if (hasError) {
        setCustomProductErrors(errors);
        return;
      }

      setAddingCustomProduct(true);

      try {
        // Trim the values before sending to API
        const trimmedProductName = customProductName.trim();
        const trimmedCucNumber = customProductCUC.trim();

        // If postItem returns a Promise, await it. If it uses callbacks, adapt accordingly.
        await postItem(
          '/product/add',
          { name: trimmedProductName, cucNumber: trimmedCucNumber },
          (data) => {
            const newProduct = {
              productId: data,
              productCucNumber: trimmedCucNumber,
              productName: trimmedProductName,
              isAffected,
              comment: productComment,
              brand: productBrand,
              countries: productCountries,
              isCustom: true
            };
            setAffectedProducts(prev => [...prev, newProduct]);
            setAvailableProducts(prev => [...prev, {
              productId: newProduct.productId,
              productCucNumber: newProduct.productCucNumber,
              productName: newProduct.productName
            }]);
            setShowAddProductDialog(false);
            resetProductForm();
          },
          'Product added successfully!',
          'Failed to add product.'
        );
      } catch (err) {
        console.error('Failed to add custom product:', err);
        dispatch(setSnackbar({ open: true, message: err?.response?.data || 'Failed to add product', error: true }));
        // don't close dialog so user can fix; keep form enabled
      } finally {
        setAddingCustomProduct(false);
      }
    } else if (selectedProduct) {
      const newProduct = {
        ...selectedProduct,
        isAffected,
        comment: productComment,
        brand: productBrand,
        countries: productCountries
      };
      setAffectedProducts(prev => [...prev, newProduct]);
      setShowAddProductDialog(false);
      resetProductForm();
    }
  };

  const resetProductForm = () => {
    setSelectedProduct(null);
    setIsAffected(true);
    setProductComment('');
    setProductCommentError('');
    setProductBrand('');
    setProductCountries([]);
    setIsCustomProduct(false);
    setCustomProductName('');
    setCustomProductCUC('');
    setCustomProductErrors({ name: '', cuc: '' });
    setAddingCustomProduct(false);
  };

  // Handle bulk imported products from Excel
  const handleBulkProductsImported = (importedProducts) => {
    if (!importedProducts || importedProducts.length === 0) return;

    // Filter out products that are already in the affected products list
    const existingCucNumbers = new Set(
      affectedProducts.map(p => p.productCucNumber || p.cucNumber)
    );

    const newProducts = importedProducts.filter(
      p => !existingCucNumbers.has(p.productCucNumber)
    );

    if (newProducts.length === 0) {
      dispatch(setSnackbar({ 
        open: true, 
        message: 'All imported products are already in the assessment.', 
        error: false 
      }));
      return;
    }

    // Add new products to affected products
    setAffectedProducts(prev => [...prev, ...newProducts]);

    // Update available products list with any new temporary products
    const newTemporaryProducts = newProducts
      .filter(p => p.isTemporary)
      .map(p => ({
        productId: p.productId,
        productCucNumber: p.productCucNumber,
        productName: p.productName
      }));

    if (newTemporaryProducts.length > 0) {
      setAvailableProducts(prev => [...prev, ...newTemporaryProducts]);
    }

    dispatch(setSnackbar({ 
      open: true, 
      message: `${newProducts.length} product(s) imported successfully.`, 
      error: false 
    }));
  };

  // Delete product handling
  const getPlansContainingProduct = (productId) => {
    return (exitPlans || []).filter(plan =>
      Array.isArray(plan.products) &&
      plan.products.some(p => (p.productId || p.id) === productId)
    );
  };

  const handleAskRemoveProduct = (productId) => {
    if (!isEditMode) {
      setProductToDelete(productId);
      setDeletePopupContent("");
      setShowDeletePopup(true);
      return;
    }

    const plansWithProduct = getPlansContainingProduct(productId);
    if (plansWithProduct.length === 0) {
      setProductToDelete(productId);
      setDeletePopupContent("");
      setShowDeletePopup(true);
      return;
    }

    let warning = "";
    plansWithProduct.forEach(plan => {
      const planProducts = plan.products || [];
      if (planProducts.length === 1) {
        warning += `Product is the last product in an Exit Plan. Deleting it will automatically delete the plan.\n`;
      } else {
        warning += `Product is included in an Exit Plan. Deleting it may require modifying the plan.\n`;
      }
    });

    setProductToDelete(productId);
    setDeletePopupContent(warning.trim());
    setShowDeletePopup(true);
  };

  const handleConfirmDeleteProduct = (productId) => {
    setDeleteLoading(true);

    const updatedProducts = affectedProducts.filter(product =>
      (product.productId || product.id) !== productId
    );

    let updatedExitPlans = exitPlans;
    let plansChanged = false;

    if (isEditMode) {
      updatedExitPlans = exitPlans.reduce((acc, plan) => {
        const planProducts = plan.products || [];
        if (planProducts.some(p => (p.productId || p.id) === productId)) {
          const newProducts = planProducts.filter(p => (p.productId || p.id) !== productId);
          if (newProducts.length === 0) {
            plansChanged = true;
            return acc;
          } else {
            plansChanged = true;
            acc.push({ ...plan, products: newProducts });
            return acc;
          }
        }
        acc.push(plan);
        return acc;
      }, []);
    }

    setAffectedProducts(updatedProducts);
    setExitPlans(updatedExitPlans);

    if (plansChanged) {
      const newResponseData = {
        ...originalResponseData,
        products: updatedProducts,
        exitPlans: updatedExitPlans
      };
      setOriginalResponseData(newResponseData);
      dispatch(setData(newResponseData));
    }

    setDeleteLoading(false);
    setShowDeletePopup(false);
    setProductToDelete(null);
    setDeletePopupContent("");
  };

  // Edit product handlers
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditProductIsAffected(product.isAffected);
    setEditProductComment(product.comment || '');
    setEditProductBrand(product.brand || '');
    setEditProductCountries(product.countries || []);
    setShowEditProductDialog(true);
  };

  const handleSaveEditProduct = () => {
    if (!editingProduct) return;

    const updatedProducts = affectedProducts.map(p => {
      if ((p.productId || p.id) === (editingProduct.productId || editingProduct.id)) {
        return {
          ...p,
          isAffected: editProductIsAffected,
          comment: editProductComment,
          brand: editProductBrand,
          countries: editProductCountries
        };
      }
      return p;
    });

    setAffectedProducts(updatedProducts);
    handleCancelEditProduct();
  };

  const handleCancelEditProduct = () => {
    setShowEditProductDialog(false);
    setEditingProduct(null);
    setEditProductIsAffected(null); // null = To be Determined (the correct RD-received default)
    setEditProductComment('');
    setEditProductBrand('');
    setEditProductCountries([]);
  };

  const saveDraft = async ({
    loadingSetter = setSavingDraft,
    successMessage = 'Draft saved successfully!',
    showNoChangesMessage = false,
    noChangesMessage = 'No changes to save.'
  } = {}) => {
    // Block if the coordinator hasn't submitted yet
    if (isCoordinatorLocked) {
      dispatch(setSnackbar({ open: true, message: 'Waiting for the R&D Coordinator to submit the product list first.', error: true }));
      return 'failed';
    }
    // Allow empty products if response is compliant
    if (affectedProducts.length === 0 && responseStatus !== 'compliant') {
      dispatch(setSnackbar({ open: true, message: 'Please add at least one product before saving draft unless marked as compliant', error: true }));
      return 'failed';
    }

    if (!hasUnsavedChanges) {
      if (showNoChangesMessage && noChangesMessage) {
        dispatch(setSnackbar({ open: true, message: noChangesMessage, error: false }));
      }
      return 'skipped';
    }

    loadingSetter?.(true);
    try {
      const draftDto = {
        id: responseId || null,
        exitRequestId: parseInt(reqId),
        products: affectedProducts.map(p => ({
          productId: p.productId || p.id || null,
          productName: p.productName || p.name || '',
          productCucNumber: p.productCucNumber || p.cucNumber || '',
          isAffected: p.isAffected,
          comment: p.comment || '',
          brand: p.brand || '',
          countryIds: (p.countries || []).map(c => c.id || c)
        })),
        additionalFeedback: feedback || '',
        isCompliant: responseStatus === 'tbd' ? null : responseStatus === 'compliant',
        categoryAssignmentId: parseInt(categoryAssignmentId),
      };

      console.log('Saving draft with DTO:', draftDto);
      const response = await axiosInstance.post(APIRoutes.RD_DRAFT_SAVE, draftDto);

      const savedDate = response.data.updatedAt || response.data.createdAt || new Date().toISOString();
      setResponseId(response.data.id);
      setIsDraft(true);
      setLastSavedAt(savedDate);
      dispatch(setReduxLastSavedAt(savedDate));
      setHasUnsavedChanges(false);
      setStatus('DRAFT');

      // Update Redux request data with DRAFT status so exit plan page can detect it
      dispatch((dispatch, getState) => {
        const current = getState().requestData.requestData;
        if (current) {
          dispatch(setData({
            ...current,
            id: response.data.id,
            status: 'DRAFT',
          }));
        }
      });

      setInitialState({
        responseStatus: responseStatus,
        feedback: feedback,
        affectedProducts: JSON.stringify(affectedProducts)
      });

      if (successMessage) {
        dispatch(setSnackbar({ open: true, message: successMessage, error: false }));
      }

      return 'saved';
    } catch (error) {
      console.error('Failed to save draft:', error);
      dispatch(setSnackbar({ open: true, message: error.response?.data || 'Failed to save draft', error: true }));
      return 'failed';
    } finally {
      loadingSetter?.(false);
    }
  };

  // Draft save handler
  const handleSaveDraft = async () => {
    return saveDraft();
  };

  // Draft delete handler
  const [showDeleteDraftDialog, setShowDeleteDraftDialog] = useState(false);

  const handleDeleteDraft = async () => {
    if (!responseId || !isDraft) return;

    try {
      await axiosInstance.delete(`${APIRoutes.RD_DRAFT_DELETE}/${responseId}`);
      dispatch(setSnackbar({ open: true, message: 'Draft deleted successfully!', error: false }));
      setShowDeleteDraftDialog(false);
      navigate(currentUserRole === 'RD_MANAGER' ? DefaultRoutes.rdManagerResponses : DefaultRoutes.rdResponses);
    } catch (error) {
      console.error('Failed to delete draft:', error);
      dispatch(setSnackbar({ open: true, message: 'Failed to delete draft', error: true }));
    }
  };

  // Submit handler
  const handleSubmit = (createExitPlan = false) => {
    setSubmitType(createExitPlan ? 'exitPlan' : 'response');

    // Block if the coordinator hasn't submitted yet
    if (isCoordinatorLocked) {
      dispatch(setSnackbar({ open: true, message: 'The R&D Coordinator must submit the product list before you can respond.', error: true }));
      setSubmitType(null);
      return;
    }

    // Allow empty products if response is compliant
    if (affectedProducts.length === 0 && responseStatus !== 'compliant') {
      alert('Please add at least one product unless marked as compliant');
      setSubmitType(null);
      return;
    }

    // Block submission while response status is still TBD
    if (responseStatus === 'tbd') {
      dispatch(setSnackbar({
        open: true,
        message: 'Please select an Impact Assessment Status (Compliant or Incompliant) before submitting.',
        error: true,
      }));
      setSubmitType(null);
      return;
    }

    // Block submission while any product is still "To be Determined"
    const tbdCount = affectedProducts.filter(p => isToBeDetermined(p.isAffected)).length;
    if (tbdCount > 0) {
      dispatch(setSnackbar({
        open: true,
        message: `${tbdCount} product(s) still need an impact decision (To be Determined) before you can submit.`,
        error: true,
      }));
      setSubmitType(null);
      return;
    }

    const exitRequestResponseDto = {
      id: responseId || null,
      exitRequestId: reqId,
      exitRequest: requestData,
      submittedBy: null,
      products: affectedProducts.map(p => ({
        productId: p.productId || p.id || null,
        productName: p.productName || p.name || '',
        productCucNumber: p.productCucNumber || p.cucNumber || '',
        isAffected: p.isAffected,
        comment: p.comment || '',
        brand: p.brand || '',
        countryIds: (p.countries || []).map(c => c.id || c)
      })),
      additionalFeedback: feedback || '',
      isCompliant: responseStatus === 'compliant',
      createdAt: createdAt,
      exitPlans: exitPlans,
      categoryAssignmentId: categoryAssignmentId,
    };
    console.log("dto ", exitRequestResponseDto);

    if (isEditMode && responseId) {
      postItem(
        `/exit-request-response/${responseId}`,
        exitRequestResponseDto,
        (updatedData) => {
          console.log("updatedData", updatedData);
          const newResponseData = { ...originalResponseData, ...exitRequestResponseDto, ...updatedData };
          console.log("newResponseData", newResponseData);
          setOriginalResponseData(newResponseData);
          dispatch(setData(newResponseData));
          setIsDraft((updatedData?.status || newResponseData?.status) === 'DRAFT');

          // FIX: Use updatedData.products directly, fallback to exitRequestResponseDto.products if missing
          setAffectedProducts(updatedData.products || exitRequestResponseDto.products || []);

          // Update initial state to reflect the new submitted state
          setInitialState({
            responseStatus: responseStatus,
            feedback: feedback,
            affectedProducts: JSON.stringify(updatedData.products || exitRequestResponseDto.products || [])
          });

          // Clear unsaved changes flag after successful submission
          setHasUnsavedChanges(false);

          if (createExitPlan && responseStatus === 'incompliant') {
            navigate(getExitPlanCreateRoute());
          } else {
            setIsEditMode(false);
          }
          setSubmitType(null);
        },
        'Impact Assessment updated successfully!',
        'Failed to update impact assessment.',
        true,
        'put'
      );
    } else {
      postItem(
        '/exit-request-response',
        exitRequestResponseDto,
        (updatedData) => {
          console.log("updatedData", updatedData);
          const newResponseData = { ...originalResponseData, ...exitRequestResponseDto, ...updatedData };
          console.log("newResponseData", newResponseData);
          setOriginalResponseData(newResponseData);
          dispatch(setData(newResponseData));
          setIsDraft((updatedData?.status || newResponseData?.status) === 'DRAFT');

          // Clear unsaved changes flag after successful submission
          setHasUnsavedChanges(false);

          if (createExitPlan && responseStatus === 'incompliant') {
            navigate(getExitPlanCreateRoute());
          } else {
            // Keep the user on this page after submitting the impact assessment
            // instead of navigating back to the incoming/response listings.
            setResponseId(newResponseData.id || updatedData?.id || responseId);
            setCreatedAt(newResponseData.createdAt || createdAt);
            setStatus(newResponseData.status || 'SUBMITTED');
            setIsEditMode(false);
          }
          setSubmitType(null);
        },
        'Impact Assessment submitted successfully!',
        'Failed to submit impact assessment.'
      );
    }
  };

  // The plan/exemption alert lives in the narrow summary column, so its message sits above the
  // actions instead of beside them; the buttons then keep their labels on one line.
  const planAlertStyles = {
    mt: 1,
    '& .MuiAlert-message': {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
  };

  const planAlertActionsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
  };

  const planAlertButtonStyles = {
    py: 0.25,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  // Exit plan status renderer
  const renderExitPlanStatus = () => {
    if (!originalResponseData || originalResponseData.isCompliant === true) {
      return null;
    }

    const plans = originalResponseData.exitPlans || [];

    // An exemption request must cover at least one impacted product, and a product can belong to only
    // one plan. Once every impacted product is covered the create form would open with nothing to
    // select, so the action is hidden rather than leading to a dead end.
    const plannedProductIds = new Set(
      plans.flatMap((plan) => (plan.products || []).map((product) => product.productId))
    );
    const unplannedImpactedCount = (originalResponseData.products || []).filter(
      (product) => product.isAffected === true && !plannedProductIds.has(product.productId)
    ).length;
    const canAddAnotherRequest = !isCoordinatorLocked && unplannedImpactedCount > 0;

    if (plans.length === 0) {
      return (
        <Alert severity="warning" sx={planAlertStyles}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            No Exit Plans or Exemption Requests created yet.
          </Typography>
          {!isCoordinatorLocked && (
            <Box sx={planAlertActionsStyles}>
              <Button
                variant="contained"
                size="small"
                color="warning"
                sx={planAlertButtonStyles}
                onClick={handleCreateExitPlanClick}
                startIcon={<EditCalendarRoundedIcon sx={{ fontSize: '0.9rem' }} />}
              >
                Create Plans
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                sx={planAlertButtonStyles}
                onClick={handleCreateExemptionClick}
                startIcon={<ReceiptLongIcon sx={{ fontSize: '0.9rem' }} />}
              >
                Create Exemption Request
              </Button>
            </Box>
          )}
        </Alert>
      );
    }

    const statusCounts = plans.reduce((acc, plan) => {
      acc[plan.status] = (acc[plan.status] || 0) + 1;
      return acc;
    }, {});
    const total = plans.length;

    const statusMessages = {
      DRAFT: { severity: 'warning', text: `${statusCounts.DRAFT} Exit Plan(s) are still in draft. Please complete and submit.` },
      REVISION_REQUIRED: { severity: 'warning', text: `${statusCounts.REVISION_REQUIRED} Exit Plan(s) require revision.` },
      REJECTED: { severity: 'error', text: `${statusCounts.REJECTED} Exit Plan(s) have been rejected.` },
      WAITING_FOR_SUPPORT: { severity: 'info', text: `${statusCounts.WAITING_FOR_SUPPORT} Exit Plan(s) are waiting for support team response.` },
      SUPPORT_PROVIDED: { severity: 'info', text: `${statusCounts.SUPPORT_PROVIDED} Exit Plan(s) have received support and are pending review.` },
      UNDER_REVIEW: { severity: 'info', text: `${statusCounts.UNDER_REVIEW} Exit Plan(s) are under review.` },
      PENDING_LOCAL_RISK_ASSESSMENT: { severity: 'info', text: `${statusCounts.PENDING_LOCAL_RISK_ASSESSMENT} Exemption Request(s) are waiting for the regulatory owner's local risk assessment.` },
      SUBMITTED: { severity: 'info', text: `${statusCounts.SUBMITTED} Exit Plan(s) / Exemption Request(s) have been submitted.` },
      APPROVED: { severity: 'success', text: statusCounts.APPROVED === total ? 'All Exit Plans / Exemption Requests are final.' : `${statusCounts.APPROVED} Exemption Request(s) approved.` }
    };

    const priority = ['DRAFT', 'REVISION_REQUIRED', 'REJECTED', 'WAITING_FOR_SUPPORT', 'SUPPORT_PROVIDED', 'UNDER_REVIEW', 'PENDING_LOCAL_RISK_ASSESSMENT', 'SUBMITTED', 'APPROVED'];
    const currentStatus = priority.find(status => statusCounts[status]);

    if (!currentStatus) {
      return (
        <Alert severity="info" sx={planAlertStyles}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            Exit Plans are in multiple states. Please review each plan for details.
          </Typography>
          <Box sx={planAlertActionsStyles}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              sx={planAlertButtonStyles}
              onClick={handleViewExitPlanClick}
            >
              View Plans
            </Button>
            {canAddAnotherRequest && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                sx={planAlertButtonStyles}
                onClick={handleCreateExemptionClick}
                startIcon={<ReceiptLongIcon sx={{ fontSize: '0.9rem' }} />}
              >
                Add Exemption Request
              </Button>
            )}
          </Box>
        </Alert>
      );
    }

    const statusInfo = statusMessages[currentStatus];
    return (
      <Alert severity={statusInfo.severity} sx={planAlertStyles}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {statusInfo.text}
        </Typography>
        <Box sx={planAlertActionsStyles}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            sx={planAlertButtonStyles}
            onClick={handleViewExitPlanClick}
          >
            View Plans
          </Button>
          {canAddAnotherRequest && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={planAlertButtonStyles}
              onClick={handleCreateExemptionClick}
              startIcon={<ReceiptLongIcon sx={{ fontSize: '0.9rem' }} />}
            >
              Add Exemption Request
            </Button>
          )}
        </Box>
      </Alert>
    );
  };
  // Effect: check all plans and set editDisabled accordingly
  useEffect(() => {
    const hasSubmitted = (exitPlans || []).some(plan => plan.status === 'SUBMITTED');
    // Also check if status is COMPLETED
    if (status === "COMPLETED" || hasSubmitted) {
      setEditDisabled(true);
      setIsEditMode(false);
    } else {
      setEditDisabled(false);
    }
  }, [exitPlans, status]);

  // Add this effect after all useState/useEffect hooks, before return
  useEffect(() => {
    if (!isEditMode) return;
    if (affectedProducts.length === 0) return;
    // A product with isAffected === true is confirmed impacted.
    // Products with isAffected === null are still "To be Determined" — don't treat them as compliant.
    const anyImpacted = affectedProducts.some(p => p.isAffected === true);
    const anyTBD = affectedProducts.some(p => p.isAffected === null || p.isAffected === undefined);
    if (anyImpacted && responseStatus !== 'incompliant') {
      setResponseStatus('incompliant');
    } else if (!anyImpacted && !anyTBD && responseStatus !== 'compliant') {
      // Only auto-set compliant when every product has been explicitly decided as NOT impacted
      setResponseStatus('compliant');
    }
    // If anyTBD and none are impacted yet, leave the responseStatus unchanged (let the user decide after they determine all products)
  }, [affectedProducts, isEditMode]);

  // Helper: Get affected products not in any plan
  const getUnplannedAffectedProducts = () => {
    if (!affectedProducts || !exitPlans) return [];
    const plannedProductIds = exitPlans.flatMap(plan => (plan.products || []).map(p => p.productId || p.id));
    return affectedProducts.filter(p => p.isAffected === true && !plannedProductIds.includes(p.productId || p.id));
  };

  const countriesInMyRegions = useMemo(() => {
    const countries = requestData?.countries || [];
    if (!countries.length) return [];
    if (!userRegions.length) return countries;

    const normalizedRegions = userRegions.map((region) =>
      (region.name || region.regionName || region)?.toLowerCase()
    );

    return countries.filter((country) =>
      normalizedRegions.includes((country.regionName || '').toLowerCase())
    );
  }, [requestData?.countries, userRegions]);

  // Loading and error states
  if (notFound) {
    // navigate(-1);
    return null;
  }

  if (loading || !requestData || !ingredient || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingComponent size="5em" thickness={5} color="primary" />
      </Box>
    );
  }

  // Product table component
  const ProductTable = ({ products, onRemove, onEdit }) => (
    <Paper variant="outlined" sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{
        display: 'flex',
        width: '100%',
        backgroundColor: theme.palette.grey[100],
        borderBottom: `1px solid ${theme.palette.divider}`,
        zIndex: 10
      }}>
        <Box sx={{ flex: '1 0 12%', ...tableHeaderCellStyles(theme) }}>{PRODUCT_IDENTIFIER_HEADER}</Box>
        <Box sx={{ flex: '1 0 15%', ...tableHeaderCellStyles(theme) }}>Name</Box>
        <Box sx={{ flex: '1 0 10%', ...tableHeaderCellStyles(theme) }}>Brand</Box>
        <Box sx={{ flex: '1 0 15%', ...tableHeaderCellStyles(theme) }}>Countries</Box>
        <Box sx={{ flex: '1 0 10%', ...tableHeaderCellStyles(theme), textAlign: 'center' }}>Affected</Box>
        <Box sx={{ flex: '1 0 18%', ...tableHeaderCellStyles(theme) }}>Comments</Box>
        {isEditMode && <Box sx={{ flex: '0 0 10%', ...tableHeaderCellStyles(theme), textAlign: 'center' }}>Actions</Box>}
      </Box>

      <Box sx={{
        width: '100%',
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {products.map((product, index) => (
          <Box
            key={product.productId || product.id}
            sx={{
              display: 'flex',
              width: '100%',
              bgcolor: index % 2 === 1 ? theme.palette.grey[50] : 'white',
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ flex: '1 0 12%', ...tableCellStyles(theme) }}>{product.productCucNumber}</Box>
            <Box sx={{ flex: '1 0 15%', ...tableCellStyles(theme) }}>{product.productName}</Box>
            <Box sx={{ flex: '1 0 10%', ...tableCellStyles(theme) }}>
              {product.brand || (
                <Typography component="span" sx={noCommentStyles(theme)}>—</Typography>
              )}
            </Box>
            <Box sx={{ flex: '1 0 15%', ...tableCellStyles(theme), display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(product.countries || []).length > 0 ? (
                product.countries.map((c) => (
                  <Chip key={c.id || c} label={c.name || c} size="small" sx={{ ...smallChipStyles, fontSize: '0.7rem' }} />
                ))
              ) : (
                <Typography component="span" sx={noCommentStyles(theme)}>—</Typography>
              )}
            </Box>
            <Box sx={{ flex: '1 0 10%', ...tableCellStyles(theme), textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {(() => {
                const cfg = getImpactStatusConfig(product.isAffected);
                const icon = cfg.key === IMPACT_STATUS.IMPACTED
                  ? <DoDisturbIcon />
                  : cfg.key === IMPACT_STATUS.NOT_IMPACTED
                    ? <CheckCircleIcon />
                    : <HelpOutlineIcon />;
                return (
                  <Chip
                    size="small"
                    label={cfg.label}
                    color={cfg.color}
                    icon={icon}
                    sx={statusChipStyles(cfg.color)}
                  />
                );
              })()}
            </Box>
            <Box sx={{ flex: '1 0 18%', ...tableCellStyles(theme) }}>
              {product.comment || (
                <Typography component="span" sx={noCommentStyles(theme)}>
                  no comment
                </Typography>
              )}
            </Box>
            {isEditMode && (
              <Box sx={{ flex: '0 0 10%', ...tableCellStyles(theme), textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onEdit(product)}
                  title="Edit product"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemove(product.productId || product.id)}
                  title="Delete product"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );

  const anyProductImpacted = affectedProducts.some(p => p.isAffected === true);
  const tbdCount = affectedProducts.filter(p => isToBeDetermined(p.isAffected)).length;

  return (
    <PageContainer>
      {/* Page Header */}
      <HeaderContainer>
        <PageTitle
          title="Impact Assessment"
          PageIcon={<InfoIcon color="white" fontSize="large" />}
          iconColor={themeCreator().palette.primary.main}
          noMargin
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              mr: 1,
              '&:hover': {
                backgroundColor: '#ffffff',
                color: theme.palette.primary.main,
                boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
              }
            }}
          >
            Return
          </Button>
          {responseId && !editDisabled && !isCoordinatorLocked && (
            <FormControlLabel
              control={
                <ModeSwitch
                  checked={!isEditMode}
                  onChange={handleModeToggle}
                  disabled={editDisabled} // NEW: Disable switch if editDisabled
                />
              }
              label={
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: isEditMode ? theme.palette.primary.main : theme.palette.secondary.main
                }}>
                  {isEditMode ? (
                    <>
                      <EditIcon sx={{ fontSize: '0.9rem' }} />
                      <Typography component="span" sx={{ fontSize: '0.75rem' }}>Edit</Typography>
                    </>
                  ) : (
                    <>
                      <VisibilityIcon sx={{ fontSize: '0.9rem' }} />
                      <Typography component="span" sx={{ fontSize: '0.75rem' }}>View</Typography>
                    </>
                  )}
                </Box>
              }
              labelPlacement="end"
              sx={{ m: 0 }}
            />
          )}
        </Box>
      </HeaderContainer>

      {/* Coordinator Lock Banner */}
      {isCoordinatorLocked && (
        <Alert
          severity="warning"
          icon={false}
          sx={{
            mx: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.light',
            background: 'linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)',
            boxShadow: '0 2px 8px rgba(255, 167, 38, 0.15)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, mt: 0.25
              }}
            >
              <Typography sx={{ color: '#fff', fontSize: '1.2rem' }}>⏳</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#e65100', mb: 0.5 }}>
                Waiting for R&amp;D Coordinator
              </Typography>
              <Typography variant="body2" sx={{ color: '#6d4c00', lineHeight: 1.5 }}>
                The R&amp;D Coordinator has not yet submitted the product list for your assessment.
                You will be able to fill in your impact analysis once the coordinator submits.
                Please check back later.
              </Typography>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Request Details Section */}
      <ContentGridContainer>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <StyledCard sx={cardTransition(theme)}>
              <CardContent sx={{ ...cardContentStyles, py: 0.8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SectionTitle variant="h6" sx={sectionTitleStyles(theme)}>
                    Request Details
                  </SectionTitle>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ ml: 1, pb: 1.3, fontSize: '0.75rem' }}
                  >
                    Issued at: {requestData && requestData.createdAt ? new Date(requestData.createdAt).toLocaleDateString() : "-"}
                  </Typography>
                </Box>

                <Grid container spacing={1.5}>
                  {/* LEFT HALF */}
                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px', flexShrink: 0 }}>Ingredient Name:</Typography>
                          <ValueText variant="body1" sx={{ ...valueTextStyles, flex: 1 }}>{ingredient.name}</ValueText>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>CAS Number:</Typography>
                          <ValueText variant="body1" sx={valueTextStyles}>{ingredient.casNumber}</ValueText>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>Restriction:</Typography>
                          <Chip
                            label={requestData.restrictionType}
                            color={getRestrictionTypeColor(requestData.restrictionType)}
                            size="small"
                            sx={smallChipStyles}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>{requestData.primaryDateType ? requestData.primaryDateType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Primary Date"}:</Typography>
                          <ValueText variant="body1" color="error" sx={valueTextStyles}>{requestData.primaryDate ? new Date(requestData.primaryDate).toLocaleDateString() : "-"}</ValueText>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>{requestData.secondaryDateType ? requestData.secondaryDateType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Secondary Date"}:</Typography>
                          <ValueText variant="body1" sx={{ ...valueTextStyles, color: theme.palette.warning.main }}>{requestData.secondaryDate ? new Date(requestData.secondaryDate).toLocaleDateString() : "-"}</ValueText>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>Issued By:</Typography>
                          <ValueText variant="body1" sx={valueTextStyles}>{user.username}</ValueText>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 0, minWidth: '80px' }}>Email:</Typography>
                          <ValueText variant="body1">
                            <a href={`mailto:${user.email}`} style={{ color: theme.palette.primary.main }}>
                              {user.email}
                            </a>
                          </ValueText>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Category</Typography>
                        <ValueText variant="body1" sx={valueTextStyles}>
                          {requestData.categoryName || requestData?.exitRequest?.categoryName || '-'}
                        </ValueText>
                      </Grid>
                      {categoryDescription && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Restrictions Descriptions</Typography>
                          <Paper variant="outlined" sx={{ ...descriptionPaperStyles(theme), maxHeight: '80px' }}>
                            <Typography variant="body2" sx={valueTextStyles}>{categoryDescription}</Typography>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* RIGHT HALF: DESCRIPTION */}
                  <Grid item xs={12} sm={6} pt={0} style={{paddingTop:"0px"}}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Description</Typography>
                        <Paper
                          variant="outlined"
                          sx={descriptionPaperStyles(theme)}
                        >
                          <Typography variant="body2" sx={valueTextStyles}>
                            {ingredient.description}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Additional Info</Typography>
                        <Paper
                          variant="outlined"
                          sx={descriptionPaperStyles(theme)}
                        >
                          <Typography variant="body2" sx={valueTextStyles}>
                            {requestData.additionalInfo && requestData.additionalInfo.trim() !== ""
                              ? requestData.additionalInfo
                              : "No additional information provided."}
                          </Typography>
                        </Paper>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Countries</Typography>
                          {countriesInMyRegions.length ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {countriesInMyRegions.map((country) => (
                                <Chip
                                  key={country.id || country.countryCode}
                                  label={`${country.name}`}
                                  size="small"
                                  color="primary"
                                  sx={{ minWidth: 0 }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <ValueText variant="body1" sx={{ ...valueTextStyles, mt: 0.5 }}>
                              No assigned countries for your regions
                            </ValueText>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </ContentGridContainer>

      {/* Product Assessment and Impact Assessment Summary Sections */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 1, mt: 0.5 }}>
        {/* Product Assessment Section - LEFT */}
        <Box sx={{ flex: '1 1 65%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <StyledCard sx={{ ...cardTransition(theme), display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ ...cardContentStyles, py: 0.8, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <SectionTitle variant="h6" sx={sectionTitleStyles(theme)}>
                Product Assessment
              </SectionTitle>

              {/* Bulk Import Section - only show in edit mode and not locked */}
              {isEditMode && !isCoordinatorLocked && (
                <ExcelBulkImport
                  onProductsImported={handleBulkProductsImported}
                  existingProducts={affectedProducts}
                  availableProducts={availableProducts}
                  downloadDraftUrl={responseId ? `/exit-request-response/draft/${reqId}/${categoryAssignmentId}/download-excel` : null}
                />
              )}

              {affectedProducts.length === 0 && isEditMode ? (
                <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.grey[50], flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <InfoIcon sx={{ color: theme.palette.grey[500], mr: 1, fontSize: '1rem' }} />
                    <Typography variant="body2" color="textSecondary" sx={valueTextStyles}>
                      {responseStatus === 'compliant' 
                        ? "No products added. For compliant impact assessments, products are optional."
                        : (responseId ? "An impact assessment must contain at least one product unless marked as compliant" : "No products added yet. Click Add Product to begin assessment.")}
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <ProductTable products={affectedProducts} onRemove={handleAskRemoveProduct} onEdit={handleEditProduct} />
              )}

              {/* Add Product button - only show in edit mode and not locked */}
              {isEditMode && !isCoordinatorLocked && (
                <Box sx={{
                  position: 'absolute',
                  right: '20px',
                  top: '10px',
                  zIndex: 1,
                }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => setShowAddProductDialog(true)}
                    sx={addButtonStyles(theme)}
                  >
                    Add Product
                  </Button>
                </Box>
              )}
            </CardContent>
          </StyledCard>
        </Box>

        {/* Impact Assessment Summary Section - RIGHT */}
        <Box sx={{ flex: '1 1 35%', display: 'flex', flexDirection: 'column' }}>
          <StyledCard sx={{ ...cardTransition(theme), height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ ...cardContentStyles, py: 0.8, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <SectionTitle variant="h6" sx={sectionTitleStyles(theme)}>Impact Assessment Summary</SectionTitle>

              {/* Scrollable content container */}
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                minHeight: 0,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: theme.palette.grey[600],
                }
              }}>
                {/* Alert if editing is disabled, with cause-specific message */}
                {editDisabled && (
                  <>
                    {(exitPlans || []).some(plan => plan.status === 'SUBMITTED') ? (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        <Typography variant="body2">
                          Due to at least one Exit Plan being submitted, the impact analysis cannot be edited.
                        </Typography>
                      </Alert>
                    ) : status === "COMPLETED" ? (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        <Typography variant="body2">
                          This response is marked as completed. Impact analysis can no longer be edited.
                        </Typography>
                      </Alert>
                    ) : null}
                  </>
                )}

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {isEditMode ? (
                    <>
                      <FormControl fullWidth size="small" sx={{ mb: 1.5, mt: 1 }}>
                        <InputLabel id="response-status-label">Impact Assessment Status</InputLabel>
                        <Select
                          labelId="response-status-label"
                          id="response-status"
                          value={responseStatus}
                          label="Impact Assessment Status"
                          onChange={(e) => setResponseStatus(e.target.value)}
                          disabled={anyProductImpacted} // lock if any product is impacted
                        >
                          <MenuItem value="tbd" disabled>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <HelpOutlineIcon color="action" sx={{ mr: 1, fontSize: '0.9rem' }} />
                              <Typography sx={valueTextStyles}>TBD</Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="compliant">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: '0.9rem' }} />
                              <Typography sx={valueTextStyles}>Compliant</Typography>
                            </Box>
                          </MenuItem>
                          <MenuItem value="incompliant">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <DoDisturbIcon color="error" sx={{ mr: 1, fontSize: '0.9rem' }} />
                              <Typography sx={valueTextStyles}>Incompliant</Typography>
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {responseStatus === 'incompliant' && (
                        <Alert severity="info" sx={infoAlertStyles}>
                          <Typography sx={{ fontSize: '0.8rem' }}>
                            If you select "Incompliant", you will need to create an exit plan after submitting this response.
                          </Typography>
                        </Alert>
                      )}

                      <TextField
                        label="Feedback/Additional Comments"
                        multiline
                        rows={responseStatus === 'incompliant' ? 2.5 : 2.5}
                        fullWidth
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Provide any additional context or feedback regarding your impact assessment..."
                        required={false}
                        size="small"
                        sx={{ flex: 1, minHeight: 0 }}
                      />
                    </>
                  ) : (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Impact Assessment Status</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {originalResponseData?.isCompliant === null || originalResponseData?.isCompliant === undefined ? (
                            <Chip
                              label="TBD"
                              color="warning"
                              icon={<HelpOutlineIcon />}
                              size="small"
                              sx={statusChipStyles('warning')}
                            />
                          ) : originalResponseData?.isCompliant ? (
                            <Chip
                              label="Compliant"
                              color="success"
                              icon={<CheckCircleIcon />}
                              size="small"
                              sx={statusChipStyles('success')}
                            />
                          ) : (
                            <Chip
                              label="Incompliant"
                              color="error"
                              icon={<DoDisturbIcon />}
                              size="small"
                              sx={statusChipStyles('error')}
                            />
                          )}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Feedback/Comments</Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          ...descriptionPaperStyles(theme),
                          flex: 1,
                          minHeight: 0
                        }}
                      >
                        <Typography variant="body2" sx={valueTextStyles}>
                          {originalResponseData?.additionalFeedback || 'No feedback provided'}
                        </Typography>
                      </Paper>

                      <Box sx={{ pt: 1 }}>
                        {renderExitPlanStatus()}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      </Box>

      {/* Draft Status Indicator and Confirmation Checkbox Row */}
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, mb: 2 }}>
        {/* Left side: Draft status and warnings */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 65%' }}>
          {isDraft && (
            <>
              <Chip label="DRAFT" color="warning" size="small" />
              {lastSavedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last saved: {new Date(lastSavedAt).toLocaleString()}
                </Typography>
              )}
              {hasUnsavedChanges && (
                <Typography variant="caption" color="warning.main">
                  • Unsaved changes
                </Typography>
              )}
            </>
          )}
          {getUnplannedAffectedProducts().length > 0 && (
            <Alert severity="warning" sx={{ py: 0.5, px: 1 }}>
              <Typography variant="caption">
                There are affected products not included in any exit plan. Please ensure all products are planned.
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Right side: Confirmation checkbox */}
        {isEditMode && !editDisabled && !isCoordinatorLocked && (
          <Box sx={{
            flex: '1 1 35%',
            display: 'flex',
            alignItems: 'center',
            border: `2px solid ${theme.palette.primary.main}`,
            borderRadius: '12px',
            px: 2,
            py: 1,
            backgroundColor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.04)'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                  I confirm and take responsibility that all impacted products have been listed in this impact analysis
                </Typography>
              }
              sx={{
                alignItems: 'center',
                m: 0,
                width: '100%',
                '& .MuiFormControlLabel-label': {
                  lineHeight: 1.3,
                }
              }}
            />
          </Box>
        )}
      </Box>

      {/* Submit Buttons - Only show in edit mode and not coordinator-locked */}
      {isEditMode && !editDisabled && !isCoordinatorLocked && (
        <SubmitButtonContainer sx={{ pt: 0.5 }}>
          <ExtensionStatusBanner categoryAssignmentId={categoryAssignmentId} />
          {tbdCount > 0 && (
            <Alert severity="warning" sx={{ mb: 1, width: '100%' }}>
              {tbdCount} product(s) are still "To be Determined." Open each and set Impacted or Not Impacted before submitting.
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setExtensionDialogOpen(true)}
              sx={actionButtonStyles(theme)}
            >
              Request Extension
            </Button>

            {/* Only show Save as Draft if the response is a draft or hasn't been submitted yet */}
            {(isDraft || !responseId) && (
              <LoadingButton
                variant="outlined"
                size="large"
                loading={savingDraft}
                onClick={handleSaveDraft}
                disabled={
                  (affectedProducts.length === 0 && responseStatus !== 'compliant') ||
                  loading ||
                  submitting ||
                  savingDraft ||
                  !hasUnsavedChanges
                }
                sx={actionButtonStyles(theme)}
              >
                {responseId ? 'Update Draft' : 'Save as Draft'}
              </LoadingButton>
            )}

            <LoadingButton
              variant="outlined"
              size="large"
              loading={submitting && submitType === 'response'}
              onClick={() => {
                setPendingSubmitCreatePlan(false);
                setShowSubmitConfirmDialog(true);
              }}
              disabled={
                !confirmationChecked ||
                (responseStatus === 'incompliant' && affectedProducts.length === 0) ||
                (responseId && !isChanged && !isDraft) ||
                loading ||
                (submitting && submitType) ||
                affectedProducts.some(p => isToBeDetermined(p.isAffected))
              }
              sx={actionButtonStyles(theme)}
            >
              {isDraft ? 'Submit Impact Assessment' : (responseId ? 'Update Impact Assessment' : 'Submit Impact Assessment')}
            </LoadingButton>

            {isDraft && (
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={() => setShowDeleteDraftDialog(true)}
                sx={actionButtonStyles(theme)}
              >
                Delete Draft
              </Button>
            )}
          </Box>
        </SubmitButtonContainer>
      )}

      {/* Add Product Dialog */}
      <Dialog
        open={showAddProductDialog}
        onClose={() => !addingCustomProduct && setShowAddProductDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <Tabs
            value={isCustomProduct ? 1 : 0}
            onChange={(_, newValue) => setIsCustomProduct(newValue === 1)}
            variant="fullWidth"
            sx={{
              borderBottom: `2px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.grey[50],
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.primary.main,
                height: '4px'
              },
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                transition: 'all 0.3s ease',
                color: theme.palette.text.secondary,
                py: 2,
                '&:hover': {
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.primary.main
                },
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 700
                }
              }
            }}
          >
            <Tab label="Select From List" />
            <Tab label="Add Custom Product" />
          </Tabs>

          <Box sx={{ p: 2, pt: 1 }}>
            {!isCustomProduct ? (
              <>
                <Autocomplete
                  options={availableProducts.filter(product =>
                    !affectedProducts.some(ap => ap.productId === product.productId)
                  )}
                  getOptionLabel={(option) => `${option.productCucNumber} - ${option.productName}`}
                  value={selectedProduct}
                  onChange={(_, newValue) => setSelectedProduct(newValue)}
                  noOptionsText={loadingProducts ? '' : 'No products available or all products already added'}
                  loading={loadingProducts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select From List"
                      fullWidth
                      margin="normal"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loadingProducts ? <LoadingComponent size="1.5em" thickness={4} color="primary" /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                {selectedProduct && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Product identifier type: <strong>{PRODUCT_IDENTIFIER_LABEL}</strong>
                  </Alert>
                )}
              </>
            ) : (
              <>
                <TextField
                  label={PRODUCT_IDENTIFIER_LABEL}
                  fullWidth
                  margin="normal"
                  required
                  value={customProductCUC}
                  onChange={(e) => setCustomProductCUC(e.target.value)}
                  error={!!customProductErrors.cuc}
                  helperText={customProductErrors.cuc}
                  placeholder="Enter CUC, FG, or Packaging Code"
                  disabled={addingCustomProduct}
                />

                <TextField
                  label="Product Name"
                  fullWidth
                  margin="normal"
                  required
                  value={customProductName}
                  onChange={(e) => setCustomProductName(e.target.value)}
                  error={!!customProductErrors.name}
                  helperText={customProductErrors.name}
                  placeholder="Enter product name (e.g., New Facial Cleanser)"
                  disabled={addingCustomProduct}
                />
              </>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={isAffected}
                  onChange={(e) => setIsAffected(e.target.checked)}
                  color="primary"
                  disabled={addingCustomProduct}
                />
              }
              label={isAffected ? "Product is impacted" : "Product is NOT impacted"}
              sx={{ my: 2, display: 'block' }}
            />

            <TextField
              label="Impact Comments"
              multiline
              rows={3}
              fullWidth
              value={productComment}
              onChange={(e) => setProductComment(e.target.value)}
              placeholder={isAffected ? "Describe how this product is impacted (optional)..." : "Optional comments about non-impacted product"}
              required={false}
              error={!!productCommentError}
              helperText={productCommentError}
              margin="normal"
              disabled={addingCustomProduct}
            />

            <TextField
              label="Brand"
              fullWidth
              value={productBrand}
              onChange={(e) => setProductBrand(e.target.value)}
              placeholder="Enter brand name (optional)"
              margin="normal"
              disabled={addingCustomProduct}
            />

            <Autocomplete
              multiple
              options={countriesInMyRegions}
              getOptionLabel={(option) => option.name || ''}
              value={productCountries}
              onChange={(_, newValue) => setProductCountries(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={addingCustomProduct}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Countries"
                  placeholder="Select countries (optional)"
                  margin="normal"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddProductDialog(false);
              resetProductForm();
            }}
            sx={cancelDialogButtonStyles(theme)}
            disabled={addingCustomProduct}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddProduct}
            variant="contained"
            color="primary"
            disabled={((!selectedProduct && !isCustomProduct) || addingCustomProduct)}
            sx={dialogButtonStyles(theme)}
            startIcon={addingCustomProduct && (
              <CircularProgress size={16} color="inherit" />
            )}
          >
            {addingCustomProduct ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog
        open={showEditProductDialog}
        onClose={handleCancelEditProduct}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={dialogTitleStyles(theme)}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon sx={{ mr: 1 }} />
            Edit Product
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {editingProduct && (
              <>
                <TextField
                  label={PRODUCT_IDENTIFIER_LABEL}
                  fullWidth
                  margin="normal"
                  value={editingProduct.productCucNumber || ''}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />

                <TextField
                  label="Product Name"
                  fullWidth
                  margin="normal"
                  value={editingProduct.productName || ''}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={editProductIsAffected === true}
                      onChange={(e) => setEditProductIsAffected(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    editProductIsAffected === null
                      ? "To be Determined — toggle to set impact"
                      : editProductIsAffected
                        ? "Product is impacted"
                        : "Product is NOT impacted"
                  }
                  sx={{ my: 2, display: 'block' }}
                />

                <TextField
                  label="Impact Comments"
                  multiline
                  rows={3}
                  fullWidth
                  value={editProductComment}
                  onChange={(e) => setEditProductComment(e.target.value)}
                  placeholder={
                    editProductIsAffected === null
                      ? "Set impact status above, then add comments (optional)..."
                      : editProductIsAffected
                        ? "Describe how this product is impacted (optional)..."
                        : "Optional comments about non-impacted product"
                  }
                  margin="normal"
                />

                <TextField
                  label="Brand"
                  fullWidth
                  value={editProductBrand}
                  onChange={(e) => setEditProductBrand(e.target.value)}
                  placeholder="Enter brand name (optional)"
                  margin="normal"
                />

                <Autocomplete
                  multiple
                  options={countriesInMyRegions}
                  getOptionLabel={(option) => option.name || ''}
                  value={editProductCountries}
                  onChange={(_, newValue) => setEditProductCountries(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Countries"
                      placeholder="Select countries (optional)"
                      margin="normal"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        size="small"
                        label={option.name}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelEditProduct}
            sx={cancelDialogButtonStyles(theme)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditProduct}
            variant="contained"
            color="primary"
            sx={dialogButtonStyles(theme)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Product Popup */}
      {DeletePopup(
        "product",
        showDeletePopup,
        () => {
          setShowDeletePopup(false);
          setProductToDelete(null);
          setDeletePopupContent("");
        },
        handleConfirmDeleteProduct,
        productToDelete,
        deleteLoading,
        "Delete",
        deletePopupContent
      )}

      {/* Delete Draft Confirmation Dialog */}
      <DraftConfirmationDialog
        open={showDeleteDraftDialog}
        onClose={() => setShowDeleteDraftDialog(false)}
        onConfirm={handleDeleteDraft}
        title="Delete Draft"
        message="Are you sure you want to delete this draft? This action cannot be undone."
      />

      {extensionDialogOpen && (
        <ExtensionRequestDialog
          open={extensionDialogOpen}
          onClose={() => setExtensionDialogOpen(false)}
          exitRequestId={Number(reqId)}
          categoryAssignmentId={Number(categoryAssignmentId)}
          onSubmitted={() =>
            dispatch(setSnackbar({
              open: true,
              message: "Extension request submitted.",
              error: false,
            }))
          }
        />
      )}

      {/* Submit Response Confirmation Dialog */}
      <Dialog
        open={showSubmitConfirmDialog}
        onClose={() => setShowSubmitConfirmDialog(false)}
        PaperProps={{
          sx: {
            minWidth: 450,
            maxWidth: 600,
          }
        }}
      >
        <DialogTitle>
          {isDraft ? 'Submit Impact Assessment?' : (responseId ? 'Update Impact Assessment?' : 'Submit Impact Assessment?')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {isDraft ? 'submit' : (responseId ? 'update' : 'submit')} this exit request impact assessment?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitConfirmDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowSubmitConfirmDialog(false);
              handleSubmit(pendingSubmitCreatePlan);
            }}
            color="primary"
            variant="contained"
          >
            {isDraft ? 'Yes, Submit' : (responseId ? 'Yes, Update' : 'Yes, Submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit handler */}
      {/* <Box sx={{ display: 'none' }}>
        <LoadingButton
          loading={submitting && submitType === 'exitPlan'}
          onClick={() => handleSubmit(true)}
        />
      </Box> */}

      {/* Navigation Guard Dialog */}
      <Dialog open={showNavigationDialog} onClose={handleCancelNavigation}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            {isDraft || !responseId 
              ? "You have unsaved changes. What would you like to do?" 
              : "You have unsaved changes. Please save your changes using the submit button on this page, or discard them to continue."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDiscardAndNavigate} color="error">
            Discard Changes
          </Button>
          {(isDraft || !responseId) && (
            <Button onClick={handleSaveDraftAndNavigate} variant="contained" color="primary">
              Save Draft
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ExitRequestResponseUnified;
